import moment from 'moment';
import randomColor from 'randomcolor';
import {useCallback, useEffect, useState} from 'react';
import useStore from '../../../hooks/use-store';
import useServices from '../../../hooks/use-services';
import {findEventGeofence, posicaoDoEvento} from '../../../helpers/geofenceEvent';

// O mapa precisa de um ponto para centralizar. Sem posicao no evento, o menos errado e mostrar onde
// o veiculo esta -- a tela continua util, e o card ja sabe distinguir uma da outra.
export const ancoraDaCena = (posicao, device) =>
    posicao || (device && device.currentPosition) || null;

/**
 * Cena do evento no mapa: traço até o evento, veículo, popup e centralização.
 *
 * BUSINESS RULE: `setEvent(event, click)` redesenha a cena quando o par muda; o mesmo evento com um
 * valor de clique novo (`helpers/notificationClick`) é um par novo, então um clique repetido no mesmo
 * card centraliza o mapa de novo. O traço some e volta enquanto as posições são consultadas outra vez.
 * CONSTRAINT: o mesmo par `{event, click}` mantém o estado e não redesenha; sem isso toda reexecução do
 * efeito da tela refaria a cena. `setEvent(null)` limpa.
 * CONSTRAINT: um desenho ainda em andamento quando outro começa, ou quando a tela sai do evento, não
 * escreve mais nada no mapa nem no store ao terminar.
 * BUSINESS RULE: evento de cerca cuja cerca está em `state.geofences` termina com o ajuste à cerca no lugar
 * de centralizar no ponto, e o realce sai junto com o traço; ver docs/notificacao-de-cerca.md. A chegada da
 * lista depois de um evento de cerca refaz a cena uma vez; os demais eventos não são refeitos.
 *
 * @param {Object} state estado do mapa (`instance`, `deviceMap`, `geofences`)
 * @param {Function} dispatch não usado
 * @param {Object} instance mapa
 * @return {{setEvent: Function, geofenceFocus: Object|null}} `setEvent(event, click)`, o setter do evento
 * mostrado, e `geofenceFocus`, o par `{event, click}` cujo ajuste à cerca chegou ao mapa (um valor novo
 * a cada ajuste, para a tela reagir também ao clique repetido; `null` até o primeiro)
 */
const useMapEvent = (state, dispatch, instance) => {
    const {instance: map, deviceMap, geofences} = state;
    const [shown, setShown] = useState();
    const [geofenceFocus, setGeofenceFocus] = useState(null);
    const event = shown ? shown.event : null;
    // A cena depende da cerca do evento, não da lista: evento que não é de cerca (cerca `null`) não é
    // refeito quando a lista chega, e a cerca de um evento de cerca muda uma vez, de `null` para ela.
    const geofence = findEventGeofence(geofences, event);
    const setEvent = useCallback((next, click = null) => setShown((previous) => {
        if (!next) return null;
        return previous && previous.event === next && previous.click === click ? previous : {event: next, click};
    }), []);
    const {positionService} = useServices();
    const {store} = useStore();
    const {events} = store;

    useEffect(() => {
        if (map) {
            map.initTraceEventLayer();
        }
    }, [map]);

    const doBuffer = useCallback(
        (positions) =>
            positions.map(({longitude, latitude, course}) => [
                longitude,
                latitude,
                course,
            ]),
        [],
    );

    // A tela do evento e o registro daquele instante, entao nao acompanha o veiculo: nada de assinar
    // o socket de posicoes aqui. O que fica na tela e o que foi carregado ao abrir.
    useEffect(() => {
        return () => {
            if (map && event) {
                map.removeFeaturesEventTrace();
                map.clearGeofenceFocus();
                events.setBuffer([]);
            }
        };
    }, [map, shown, event, events]);

    useEffect(() => {
        if (event && map && deviceMap) {
            map.removeFeaturesEventTrace();
            map.removeDevices();

            const {deviceId, eventTime} = event;
            const device = store.devices.findById(+deviceId);
            let active = true;

            (async () => {
                try {
                    const eventPosition = await posicaoDoEvento(positionService, event.positionId);
                    if (!active) return;

                    // A janela do traco comeca na hora do evento. Com o campo errado ela comecava
                    // "agora" -- intervalo invertido, resposta vazia, e a tela caia no atalho
                    // abaixo, que mostra a posicao atual do veiculo em vez do caminho ate o evento.
                    let positions = await positionService.getPositionsFromTo(
                        +deviceId,
                        moment(eventTime).toISOString(),
                        moment(device.lastUpdate).toISOString(),
                    );
                    if (!active) return;

                    positions = positions || [];
                    if (!positions.length && device.positionId) {
                        positions = await positionService.getPositionsByIds([device.positionId]);
                        if (!active) return;
                    }

                    events.setCurrentDevice(+deviceId);
                    events.setCurrent({...event});

                    positions = Array.isArray(positions) ? positions.reverse() : [];

                    events.setBuffer(eventPosition ? [...positions, eventPosition] : positions);
                    // A posicao do evento fica em `events.position`, e e dela que a tela vive. Antes
                    // isso tambem sobrescrevia a posicao atual do dispositivo no store, que e o
                    // mesmo no usado pelo mapa e pelas listas -- abrir um evento mexia no estado do
                    // resto do sistema, e o card mostrava uma posicao que nao era a do evento.
                    events.setPosition(eventPosition);

                    map.addFeatureDevice(deviceMap.get(deviceId));

                    const ancora = ancoraDaCena(eventPosition, device);
                    if (ancora) {
                        const coord = [ancora.longitude, ancora.latitude];

                        const overlay = map.instance.getOverlayById('overlayEvent');

                        if (!overlay) {
                            map.addEventOverlay('popupEvent', coord);
                        } else {
                            map.updateOverlayPosition(null, coord, overlay);
                        }

                        map.addFeatureEventTrace(
                            deviceId,
                            doBuffer(events.positionBuffer.toJSON()),
                            randomColor({luminosity: 'dark'}),
                        );
                    }

                    // O ajuste à cerca vem depois do popup, que também move o mapa, e substitui o
                    // `goTo`: os dois animam a visão em paralelo e o último a terminar a leva.
                    const focused = geofence && map.focusGeofence(
                        geofence,
                        eventPosition ? [eventPosition.longitude, eventPosition.latitude] : null,
                    );
                    if (focused) setGeofenceFocus(shown);
                    else if (ancora) map.goTo([ancora.longitude, ancora.latitude], 18);
                } catch (erro) {
                    // Sem isto uma chamada recusada deixava a tela pela metade: sem marcador, sem
                    // veiculo desenhado e sem responder a clique, porque o desenho parava no meio.
                    if (active) console.error('Failed to render event screen', erro);
                }
            })();

            return () => {
                active = false;
            };
        }
    }, [map, deviceMap, geofence, shown, event]);

    return {setEvent, geofenceFocus};
};

export default useMapEvent;
